import ReloadButton from 'CMFW/button/ReloadButton';
import ModuleContext from 'CMFW/context/ModuleContext';
import Grid from 'CMFW/grid/Grid';
import { LoadingItem } from 'CMFW/layout/Loading';
import {
  ContainerLeft,
  ContainerLeftFooter,
  ContainerRight,
  ModuleContainer,
} from 'components/common/styles';
import { Split } from 'components/Split';
import { useForceRender } from 'hooks/useForceRender';
import React, { useEffect, useMemo, useState } from 'react';
import { MenuItem, TextField } from '@material-ui/core';
import styled from 'styled-components';
import TranslationUnifiedProjectModel from '../../../models/TranslationUnifiedProjectModel';
import { IUnifiedDialect, IUnifiedLanguage } from '../../../models/TranslationUnifiedDashboardModel';
import { get } from '../../../services/request';
import { IFields } from '../../../CMFW/grid/fields';
import { asyncTaskStatusRenderer } from '../../../CMFW/grid/cellRenderers/asyncTaskStatus';
import { validationStatusRenderer } from '../../../CMFW/grid/cellRenderers/validationStatus';
import TranslationProjectPreTranslateDetails from '../../sourceAdaptation/components/TranslationProjectPreTranslateDetails';
import TranslationProjectTranslationItemsModel from '../../../models/TranslationProjectTranslationItemsModel';

interface IDialectsResponse {
  dialects: IUnifiedDialect[];
  languages: IUnifiedLanguage[];
}

const UnifiedProjects: React.FC = () => {
  const forceRender = useForceRender();

  const model = useMemo(() => new TranslationUnifiedProjectModel({ render: forceRender }), [forceRender]);
  const itemsModel = useMemo(() => new TranslationProjectTranslationItemsModel({ render: forceRender }), [forceRender]);

  const [dialectId, setDialectId] = useState<number>(0);
  const [languageId, setLanguageId] = useState<number>(0);
  const [dialects, setDialects] = useState<IUnifiedDialect[]>([]);
  const [languages, setLanguages] = useState<IUnifiedLanguage[]>([]);

  useEffect(() => {
    (async () => {
      const result = await get<IDialectsResponse>('/translation-unified/dialects');
      setDialects(result.dialects);
      setLanguages(result.languages);
    })();
  }, []);

  const handleDialectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(event.target.value);
    setDialectId(val);
    model.setDialectId(val);
    model.setLanguageId(languageId);
    if (val && languageId) {
      model.getGrid();
    }
  };

  const handleLanguageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(event.target.value);
    setLanguageId(val);
    model.setLanguageId(val);
    model.setDialectId(dialectId);
    if (val && dialectId) {
      model.getGrid();
    }
  };

  const cellRenderers = useMemo(
    () => ({
      [IFields.async_task_status]: asyncTaskStatusRenderer,
      [IFields.is_validated]: validationStatusRenderer,
    }),
    [],
  );

  const projectName = model.getItemUpdatedValue('name', `Project #${model.itemId}`);

  // Group dialects by language
  const dialectsByLanguage: { [lang: string]: IUnifiedDialect[] } = {};
  dialects.forEach((d) => {
    if (!dialectsByLanguage[d.languageName]) {
      dialectsByLanguage[d.languageName] = [];
    }
    dialectsByLanguage[d.languageName].push(d);
  });

  return (
    <ModuleContainer>
      <ModuleContext.Provider value={model}>
        <Split widthLeft={500}>
          <ContainerLeft>
            <SelectsWrapper>
              <SelectField
                select
                value={languageId}
                variant="outlined"
                label="Target Language"
                onChange={handleLanguageChange}
                size="small"
              >
                <MenuItem value={0}>Select language...</MenuItem>
                {languages.map((l) => (
                  <MenuItem key={l.id} value={l.id}>
                    {l.name}
                  </MenuItem>
                ))}
              </SelectField>
              <SelectField
                select
                value={dialectId}
                variant="outlined"
                label="Source Dialect"
                onChange={handleDialectChange}
                size="small"
              >
                <MenuItem value={0}>Select dialect...</MenuItem>
                {Object.keys(dialectsByLanguage)
                  .sort()
                  .map((lang) =>
                    dialectsByLanguage[lang].map((d) => (
                      <MenuItem key={d.dialectId} value={d.dialectId}>
                        {d.languageName} - {d.dialectName} ({d.dialectCode})
                      </MenuItem>
                    )),
                  )}
              </SelectField>
            </SelectsWrapper>
            <Grid cellRenderers={cellRenderers} />
            <ContainerLeftFooter>
              <ReloadButton />
            </ContainerLeftFooter>
          </ContainerLeft>
          <ContainerRight>
            {model.itemId && model.itemId !== model.NEW_CREATE_ID ? (
              <TranslationProjectPreTranslateDetails
                projectId={model.itemId}
                projectName={projectName}
                itemsModel={itemsModel}
                forceRender={forceRender}
              />
            ) : null}
            <LoadingItem />
          </ContainerRight>
        </Split>
      </ModuleContext.Provider>
    </ModuleContainer>
  );
};

export default UnifiedProjects;

const SelectsWrapper = styled.div`
  padding: 8px 10px;
  display: flex;
  gap: 10px;
`;

const SelectField = styled(TextField)`
  flex: 1;
  min-width: 180px;
`;
